import React, { forwardRef, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { colors, media, Theme } from '../../../core';
import Label from '../Label';
import Text from '../Text';

export interface TextInputProps {
  name?: string;
  label?: string;
  animatedLabel?: string;
  onChange?: (event: any) => void;
  onBlur?: (event: any) => void;
  width?: string;
  id?: string;
  placeholder?: string;
  defaultValue?: string;
  disabled?: boolean;
  type?: string;
  showRequired?: boolean;
  errorMessage?: string;
  icon?: React.ReactNode;
  onKeyPress?: (e: any) => void;
  errorMessageOnDisable?: string;
  note?: string;
  value?: string;
  component?: React.ReactNode;
  variant?: 'primary' | 'secondary';
  dataTip?: string;
  dataFor?: string;
}

const baseSmallLabelStyles = css`
  ${({ theme }) => `
    font-size: 12px;
    top: 0;
    color: ${theme.fontColor || colors.ash};
  `}
`;

const Container = styled.div<{ width?: string; hasEnteredText: boolean }>`
  width: ${({ width }) => width || '100%'};
  position: relative;

  ${media.tabletPortrait} {
    width: 100%;
  }

  ${({ hasEnteredText }) =>
    hasEnteredText &&
    css`
      ${AnimatedLabel} {
        ${baseSmallLabelStyles};
      }
    `}
`;

const InputWrapper = styled.div`
  position: relative;
`;

const IconWrapper = styled.div`
  position: absolute;
  right: ${({ theme }) => `calc(${theme.textInputIconRight} + 1px)` || '8px'};
  top: 0;
  height: ${({ theme }) => theme.textInputHeight || '36px'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledLabel = styled(Label)<{ variant: 'primary' | 'secondary' }>`
  ${({ theme, variant }) =>
    theme.name !== Theme.theme1 &&
    variant === 'secondary' &&
    `
      font-size: 12px;
      font-weight: 400;
      padding: 0 16px;
      margin: 0;
      position: absolute;
      top: -8px;
      line-height: 1.5;
    `}
`;

export const AnimatedLabel = styled(Label)<{ hasEnteredText: boolean }>`
  position: absolute;
  top: 50%;
  left: 16px;
  transform: translateY(-50%);
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.5px;
  line-height: 2;
  transition: top 300ms ease-in-out, left 300ms ease-in-out, font-size 300ms ease-in-out,
    opacity 300ms ease-in-out;
  color: ${({ theme }) => theme.textInputColor || colors.ash};
  z-index: -1;
`;

const buttonInputStyles = css<{
  isError?: boolean;
  icon?: React.ReactNode;
  variant?: 'primary' | 'secondary';
}>`
  height: ${({ theme }) => theme.textInputHeight || '36px'};
  border: 1px solid;
  border-radius: ${({ theme }) => theme.borderRadius || '3px'};
  background-color: ${({ theme, variant }) =>
    variant === 'secondary' && theme.textInputShowOnlyBottomBorder ? 'transparent' : colors.white};

  ${({ theme, variant }) =>
    variant === 'secondary' &&
    theme.textInputShowOnlyBottomBorder &&
    `
      border-top: 0;
      border-left: 0;
      border-right: 0;
      border-radius: 0;
  `}

  border-color: ${({ isError, theme, variant }) =>
    isError
      ? theme.errorColor
      : (variant === 'primary'
          ? theme.textInputBorderColor
          : theme.textInputSecondaryBorderColor) || colors.smoke};
  color: ${({ theme }) => theme.textInputColor || colors.ash};
  display: block;
  font-family: ${({ theme }) => theme.fontFamily || `Quicksand, sans-serif`};
  font-size: ${({ theme }) => theme.fontSize || '14px'};
  font-weight: ${({ theme }) => theme.fontWeight || 600};
  padding: ${({ theme }) => theme.textInputPadding || '8px 10px'};
  width: 100%;
  padding-right: ${({ icon, theme }) => (icon ? '30px' : theme.textInputIconRight)};

  ::placeholder {
    color: ${({ theme }) => theme.textInputPlaceholderColor || colors.smoke};
  }

  &:hover {
    box-shadow: ${({ theme }) =>
      theme.textInputHoverShadow || '0 2px 4px 0 rgba(217, 217, 217, 0.5)'};
  }

  &:active,
  &:focus {
    color: ${({ theme }) => theme.textInputFocusedColor || colors.charcoal};
    border-color: ${({ isError, theme }) =>
      isError ? theme.errorColor : theme.textInputFocusedBorderColor || colors.primary};
    outline: 0;
    & ~ ${AnimatedLabel} {
      ${baseSmallLabelStyles};
      top: ${({ variant }) => (variant === 'primary' ? '-12px' : '0')};
    }
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    & ~ ${AnimatedLabel} {
      ${baseSmallLabelStyles};
      top: ${({ variant }) => (variant === 'primary' ? '-12px' : '0')};
    }
  }

  &:disabled {
    background-color: ${({ theme, variant }) =>
      variant === 'secondary' && theme.textInputShowOnlyBottomBorder
        ? 'transparent'
        : theme.textInputDisabledBackgroundColor || colors.pearl};
    border-color: ${({ theme }) => theme.textInputDisabledBorderColor || colors.cloud};
    box-shadow: none;
    color: ${({ theme }) => theme.textInputDisabledColor || colors.smoke};
    cursor: default;
    -webkit-text-fill-color: ${({ theme }) => theme.textInputDisabledColor || colors.smoke};
    opacity: 1;
  }
`;

const InputComponent = styled.div<{
  isError?: boolean;
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
}>`
  ${buttonInputStyles};

  display: flex;
  align-items: center;

  &:focus-within {
    color: ${({ theme }) => theme.textInputFocusedColor || colors.charcoal};
    border-color: ${({ isError, theme }) =>
      isError ? theme.errorColor : theme.textInputFocusedBorderColor || colors.primary};
    outline: 0;

    & ~ ${AnimatedLabel} {
      ${baseSmallLabelStyles};
      top: ${({ variant }) => (variant === 'primary' ? '-12px' : '0')};
    }
  }

  > * {
    flex-grow: 1;
  }

  ${({ disabled, theme, variant }) =>
    disabled &&
    `
      background-color:
        ${
          variant === 'secondary' && theme.textInputShowOnlyBottomBorder
            ? 'transparent'
            : theme.textInputDisabledBackgroundColor || colors.pearl
        };
      border-color: ${theme.textInputDisabledBorderColor || colors.cloud};
      box-shadow: none;
      color: ${theme.textInputDisabledColor || colors.smoke};
      cursor: default;
      -webkit-text-fill-color: ${theme.textInputDisabledColor || colors.smoke};
      opacity: 1;

      &:active,
      &:focus {
        color: ${theme.textInputFocusedColor || colors.charcoal};
        border-color: ${theme.textInputDisabledBorderColor || colors.cloud};
        outline: 0;
      }
   `}
`;

const StyledTextInput = styled.input.attrs(({ type }: TextInputProps) => ({
  type: type ? type : 'text',
}))`
  ${buttonInputStyles}
`;

const StyledText = styled(Text)`
  margin: 0;
  margin-top: 4px;
  font-size: 12px;
  color: ${colors.smoke};
  line-height: 1.5;
  padding: ${({ theme }) => theme.textInputNotePadding || '0'};
  font-style: ${({ theme }) => theme.textInputNoteFontStyle || 'normal'};
`;

const ErrorMessage = styled(Text)`
  margin: 0;
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.errorColor};
  padding: ${({ theme }) => theme.inputErrorMessagePadding || '0'};
  line-height: 1.5;
`;

const TextInput = forwardRef(
  (
    {
      width,
      label,
      animatedLabel,
      name = '',
      onBlur,
      onChange,
      errorMessage,
      showRequired,
      icon,
      note,
      component,
      variant = 'primary',
      ...inputProps
    }: Partial<TextInputProps>,
    ref: any
  ) => {
    const [inputValue, setInputValue] = useState(inputProps.defaultValue || inputProps.value);
    const [hasEnteredText, setHasEnteredText] = useState(false);

    useEffect(() => {
      checkUserInput();
    }, [inputValue]);

    const checkUserInput = (blurEvent?: string) => {
      if (onBlur) {
        onBlur(blurEvent);
      }
      if (inputValue) {
        setHasEnteredText(true);
      } else {
        setHasEnteredText(false);
      }
    };

    return (
      <Container width={width} hasEnteredText={hasEnteredText}>
        {label && (
          <StyledLabel htmlFor={name} variant={variant}>
            {label}
            {showRequired && '*'}
          </StyledLabel>
        )}
        <InputWrapper data-tip={inputProps?.dataTip} data-for={inputProps?.dataFor}>
          {component ? (
            <InputComponent
              variant={variant}
              ref={ref}
              isError={!!errorMessage}
              className="input-wrapper"
              disabled={inputProps.disabled}
            >
              {component}
            </InputComponent>
          ) : (
            <StyledTextInput
              name={name}
              ref={ref}
              onChange={(changeEvent) => {
                setInputValue(changeEvent.target.value);
                onChange && onChange(changeEvent.target.value);
              }}
              isError={!!errorMessage}
              onBlur={(blurEvent) => checkUserInput(blurEvent.target.value)}
              onFocus={() => checkUserInput()}
              variant={variant}
              {...inputProps}
            />
          )}
          {icon && <IconWrapper>{icon}</IconWrapper>}

          {animatedLabel && (
            <AnimatedLabel htmlFor={name} hasEnteredText={hasEnteredText}>
              {animatedLabel}
              {showRequired && '*'}
            </AnimatedLabel>
          )}
        </InputWrapper>
        {note && <StyledText>{note}</StyledText>}
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      </Container>
    );
  }
);

export default TextInput;
